/**
 * CommentBubble - one entry in the customer-side conversation thread.
 *
 * Block-style cards stacked top-down, matching the agent's
 * AgentCommentBubble (TicketDetailAgentPage). The customer surface
 * uses the same shape so an agent and a customer looking at the same
 * ticket see the same conversation layout.
 *
 *   author_kind=customer            -> neutral card, label "You"
 *   author_kind=agent / api_key     -> blue card, label "Buyist Support"
 *     (any non-customer author is scrubbed to "Buyist Support" by
 *     the server; private + draft kinds never reach the customer)
 *   author_kind=system              -> small italic centered line
 *
 * Attachments tied to this comment render inside the bubble below
 * the body, mirroring the agent layout. Tile handlers + currentUserId
 * are optional; passing them lights up the Download / Attach to
 * Response / Remove hover buttons on each tile.
 *
 * The id attribute uses ``commentAnchorId(comment.id)`` so the
 * customer-mode AuditTimeline's click-to-scroll handler can target
 * the right bubble.
 */
import { useMemo } from "react";

import { commentAnchorId } from "@/components/tickets/AuditTimeline";
import { CustomerAttachmentTile } from "@/components/tickets/CustomerAttachmentTile";
import { safeHtmlProps } from "@/lib/sanitize";
import type { AttachmentResponse, CommentResponse } from "@/types/api";

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Year included so comments on multi-year-old imported tickets
  // (Freshdesk archive) are unambiguous - matches the agent surface.
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export interface CommentBubbleProps {
  comment: CommentResponse;
  /** Attachments tied to this comment. Rendered inside the bubble. */
  attachments?: AttachmentResponse[];
  /** Required when ``attachments`` is non-empty - drives the tile's
   * download flow + the remove-ownership gate. */
  ticketNumber?: number;
  currentUserId?: string | null;
  onAttachToResponse?: (attachment: AttachmentResponse) => void;
  onRemoveAttachment?: (attachment: AttachmentResponse) => void;
  attachedToDraftIds?: ReadonlySet<string>;
}

export function CommentBubble({
  comment,
  attachments,
  ticketNumber,
  currentUserId,
  onAttachToResponse,
  onRemoveAttachment,
  attachedToDraftIds,
}: CommentBubbleProps): JSX.Element {
  // Sanitizing is the most expensive thing this page does, and it used
  // to run inline in the JSX below, which meant DOMPurify re-parsed
  // every message body on EVERY render - including the ones a keystroke
  // in the reply composer triggers. A long email thread quotes itself,
  // so those bodies are large (1.98 MB across one prod ticket). The body
  // only changes when the server sends a different one, so the raw HTML
  // is the memo key. Declared above the early returns below: hooks
  // cannot sit behind a conditional.
  // See plans/2026-08-24-ticket-detail-comment-windowing.md.
  const bodyHtmlProps = useMemo(
    () => safeHtmlProps(comment.body_html ?? ""),
    [comment.body_html],
  );

  // Defense in depth: if a private comment somehow reaches the
  // customer page, refuse to render it. The server already strips
  // these (see backend/app/api/tickets_customer.py) - this is the
  // belt-and-braces frontend rail.
  if (comment.is_private) {
    return <></>;
  }

  if (comment.author_kind === "system") {
    return (
      <div
        id={commentAnchorId(comment.id)}
        className="my-2 text-center text-xs italic text-neutral-500"
      >
        {comment.body_text}
        <span className="ml-2">{formatTimestamp(comment.created_at)}</span>
      </div>
    );
  }

  const isCustomer = comment.author_kind === "customer";
  // Server scrubs agent identity to "Buyist Support"; we trust the
  // server here but never surface raw agent identifiers.
  const label = isCustomer ? "You" : comment.author_name || "Buyist Support";

  // Matches the agent surface's color scheme for parity (SPEC §9):
  //   - customer reply  -> neutral
  //   - agent reply     -> blue (public reply)
  const containerClasses = isCustomer
    ? "bg-neutral-50 border-neutral-200"
    : "bg-blue-50 border-blue-200";
  const badge = !isCustomer ? (
    <span
      data-testid="bubble-badge"
      className="inline-flex items-center rounded-full bg-blue-200 px-2 py-0.5 text-xs font-medium text-blue-800"
    >
      Public reply
    </span>
  ) : null;

  const hasAttachments = (attachments?.length ?? 0) > 0;

  return (
    <div
      id={commentAnchorId(comment.id)}
      data-author-kind={comment.author_kind}
      data-comment-id={comment.id}
      className={`group relative rounded-lg border px-4 py-3 transition-colors duration-700 ${containerClasses}`}
    >
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium text-neutral-800">{label}</span>
        {badge}
        <span className="text-neutral-400">
          {formatTimestamp(comment.created_at)}
        </span>
      </div>
      {/* As-sent subject sits between the badge row and the body
          when present. Null on every row that pre-dates the
          20260519_0021 migration, so the customer still sees the
          ticket title at the top of the page in those cases. The
          server preserves ``comment.subject`` verbatim for public
          replies so the customer sees exactly what their inbox
          showed. */}
      {comment.subject && (
        <div
          data-testid="comment-subject"
          className="mb-1 text-xs text-neutral-600"
        >
          <span className="font-semibold uppercase tracking-wide text-neutral-500">
            Subject:
          </span>{" "}
          <span className="text-neutral-800">{comment.subject}</span>
        </div>
      )}
      {comment.body_html ? (
        <div
          className="prose prose-sm max-w-none text-sm text-neutral-800"
          dangerouslySetInnerHTML={bodyHtmlProps}
        />
      ) : (
        <div className="whitespace-pre-wrap text-sm text-neutral-800">
          {comment.body_text}
        </div>
      )}
      {hasAttachments && ticketNumber !== undefined && (
        <div
          data-testid="comment-attachments"
          className="mt-3 border-t border-neutral-200/60 pt-3"
        >
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            Attachments
          </div>
          <div className="flex flex-wrap gap-3">
            {attachments!.map((a) => (
              <CustomerAttachmentTile
                key={a.id}
                attachment={a}
                ticketNumber={ticketNumber}
                currentUserId={currentUserId ?? null}
                onAttachToResponse={onAttachToResponse}
                onRemove={onRemoveAttachment}
                isAttachedToDraft={attachedToDraftIds?.has(a.id) ?? false}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CommentBubble;
